import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import { AdminAutomotiveNav } from "../automotive/AdminAutomotiveNav";
import {
  formatLeadStatus,
  formatLeadType,
  statusBadgeClass,
} from "../../../constants/automapDealerLeadConstants";
import {
  PARTNER_LEAD_STATUS_LABELS,
  getCategoryLabel,
  partnerLeadStatusBadgeClass,
} from "../../../constants/automapPartnershipConstants";
import { fetchAdminLeads } from "../../../services/automapDealerLeadApi";
import { fetchPartnerLeads } from "../../../services/automapPartnershipApi";

export default function AdminLeadsListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "partner" ? "partner" : "buyer";
  const [leads, setLeads] = useState([]);
  const [partnerLeads, setPartnerLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dealerId, setDealerId] = useState("");
  const [status, setStatus] = useState("");
  const [utmSource, setUtmSource] = useState("");
  const [utmMedium, setUtmMedium] = useState("");
  const [utmCampaign, setUtmCampaign] = useState("");
  const [landingPage, setLandingPage] = useState("");
  const [pageType, setPageType] = useState("");

  useEffect(() => {
    if (tab !== "buyer") return;
    setLoading(true);
    const params = {};
    if (dealerId.trim()) params.dealerId = dealerId.trim();
    if (status) params.status = status;
    if (utmSource.trim()) params.utmSource = utmSource.trim();
    if (utmMedium.trim()) params.utmMedium = utmMedium.trim();
    if (utmCampaign.trim()) params.utmCampaign = utmCampaign.trim();
    if (landingPage.trim()) params.landingPage = landingPage.trim();
    if (pageType) params.pageType = pageType;
    fetchAdminLeads(params)
      .then((data) => setLeads(data.leads || []))
      .catch((err) => setError(err.message || "Error al cargar leads."))
      .finally(() => setLoading(false));
  }, [tab, dealerId, status, utmSource, utmMedium, utmCampaign, landingPage, pageType]);

  useEffect(() => {
    if (tab !== "partner") return;
    setLoading(true);
    fetchPartnerLeads({ status: status || undefined })
      .then((data) => setPartnerLeads(data.leads || []))
      .catch((err) => setError(err.message || "Error al cargar leads de aliados."))
      .finally(() => setLoading(false));
  }, [tab, status]);

  return (
    <AdminPageLayout>
      <AdminPageHeader
        eyebrow="AutoMapa · Leads"
        title={tab === "partner" ? "Leads de aliados" : "Leads de concesionarios"}
        description={tab === "partner" ? "Solicitudes de servicios (financiación, seguro, SOAT, etc.)." : "Vista operativa de leads capturados en AutoMapa."}
      />
      <AdminAutomotiveNav active="leads" />
      <nav className="admin-console__nav-tabs">
        <button type="button" className={tab === "buyer" ? "is-active" : undefined} onClick={() => setSearchParams({})}>Leads compradores</button>
        <button type="button" className={tab === "partner" ? "is-active" : undefined} onClick={() => setSearchParams({ tab: "partner" })}>Leads aliados</button>
      </nav>
      <main className="admin-console__main wrap">
        {tab === "buyer" ? (
        <div className="dealer-inventory-toolbar">
          <input placeholder="Dealer ID" value={dealerId} onChange={(e) => setDealerId(e.target.value)} />
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Estado</option>
            {["new", "routed", "contacted", "sold", "lost", "archived"].map((value) => (
              <option key={value} value={value}>
                {formatLeadStatus(value)}
              </option>
            ))}
          </select>
          <input placeholder="Fuente (utm/source)" value={utmSource} onChange={(e) => setUtmSource(e.target.value)} />
          <input placeholder="Medio" value={utmMedium} onChange={(e) => setUtmMedium(e.target.value)} />
          <input placeholder="Campaña" value={utmCampaign} onChange={(e) => setUtmCampaign(e.target.value)} />
          <input placeholder="Landing page" value={landingPage} onChange={(e) => setLandingPage(e.target.value)} />
          <select value={pageType} onChange={(e) => setPageType(e.target.value)}>
            <option value="">Tipo página</option>
            {[
              "home",
              "search_results",
              "vehicle_detail",
              "dealer_profile",
              "buyer_quiz",
              "recommendation_results",
              "checklist",
              "guide",
              "seo_city",
              "seo_make",
              "seo_model",
            ].map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
          <Link className="dealer-portal-btn" to="/admin/analytics/attribution">
            Ver atribución
          </Link>
        </div>
        ) : (
          <div className="dealer-inventory-toolbar">
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Estado</option>
              {Object.keys(PARTNER_LEAD_STATUS_LABELS).map((value) => (
                <option key={value} value={value}>{PARTNER_LEAD_STATUS_LABELS[value]}</option>
              ))}
            </select>
            <Link to="/admin/partners">Aliados</Link>
          </div>
        )}
        {error ? <p className="import-error">{error}</p> : null}
        {loading ? <p className="dealer-state-box">Cargando…</p> : null}
        {!loading && tab === "buyer" && leads.length > 0 ? (
          <table className="dealer-inventory-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Dealer</th>
                <th>Comprador</th>
                <th>Tipo</th>
                <th>Fuente</th>
                <th>Medio</th>
                <th>Campaña</th>
                <th>Estado</th>
                <th>Score</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr key={lead.id}>
                  <td>{new Date(lead.createdAt).toLocaleString("es-CO")}</td>
                  <td>{lead.dealerId?.slice(0, 8)}…</td>
                  <td>{lead.buyerName}</td>
                  <td>{formatLeadType(lead.leadType)}</td>
                  <td>{lead.lastTouchSource || lead.firstTouchSource || lead.utmSource || "—"}</td>
                  <td>{lead.lastTouchMedium || lead.firstTouchMedium || lead.utmMedium || "—"}</td>
                  <td>{lead.utmCampaign || lead.firstTouchCampaign || "—"}</td>
                  <td>
                    <span className={`lead-badge ${statusBadgeClass(lead.status)}`}>
                      {formatLeadStatus(lead.status)}
                    </span>
                  </td>
                  <td>{lead.leadScore}</td>
                  <td>
                    <Link to={`/admin/leads/${lead.id}`}>Detalle</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {!loading && tab === "partner" && partnerLeads.length > 0 ? (
          <table className="dealer-inventory-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Categoría</th>
                <th>Comprador</th>
                <th>Ciudad</th>
                <th>Vehículo</th>
                <th>Aliado</th>
                <th>Estado</th>
                <th>Origen</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {partnerLeads.map((lead) => (
                <tr key={lead.id}>
                  <td>{new Date(lead.createdAt).toLocaleString("es-CO")}</td>
                  <td>{getCategoryLabel(lead.category)}</td>
                  <td>{lead.buyerName}</td>
                  <td>{lead.buyerCity || "—"}</td>
                  <td>{lead.vehicleLabel}</td>
                  <td>{lead.partnerName}</td>
                  <td><span className={partnerLeadStatusBadgeClass(lead.status)}>{PARTNER_LEAD_STATUS_LABELS[lead.status]}</span></td>
                  <td>{lead.sourcePage || "—"}</td>
                  <td><Link to={`/admin/partners/leads/${lead.id}`}>Abrir</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {!loading && tab === "buyer" && leads.length === 0 ? <p className="dealer-state-box">Sin leads para estos filtros.</p> : null}
        {!loading && tab === "partner" && partnerLeads.length === 0 ? <p className="dealer-state-box">Sin leads de aliados.</p> : null}
      </main>
    </AdminPageLayout>
  );
}
